import {
  Point,
  Range,
  Path,
  Node,
  Editor,
  Text,
  Transforms,
} from 'domains/reporter/RichTextEditor/core';
import equals from 'ramda/es/equals';
import { find } from '../../utils/find';
import type { NodeEntry } from '../../core';
import { DEEP_LINK_PLUGIN_ID } from './types';
import type { DeepLinkPluginElement, DeepLink } from './types';
import { unreachableCaseError } from 'types';
import {
  DEFAULT_STUDY_DATE_VALUE,
  MISSING_STUDY_DATE,
  MISSING_STUDY_DESCRIPTION,
  DeepLinkVariants,
  createEmptyTextNode,
  createDelimiterTextNode,
  REPLACEABLE_DEFAULT_COMPARISON_TEXTS,
} from './constants';
import { INLINE_BOOKMARK_PLUGIN_ID } from '../inlineBookmark/types';
import type { InlineBookmarkPluginElement } from '../inlineBookmark/types';
import { isBookmarkTextUnchangedFromTemplate } from '../inlineBookmark/utils';
import type { TemplateBookmarkTextMap } from '../inlineBookmark/utils';
import { PageTypes } from 'utils/pageTypes';
import { getComparisonHeading, getNextHeadingOrHeadingLikeNode } from '../heading/utils';
import { compareAsc } from 'date-fns/compareAsc';
import { compareDesc } from 'date-fns/compareDesc';
import { toLocaleString } from 'utils/date';
import type { HeadingKeywords } from 'generated/graphql';
import type { ReviewedStudyByDate } from 'domains/viewer/ViewportDre/modules/imaging/useTrackViewedSlices';
import { Key, ReactEditor } from 'slate-react';
import { logger } from 'modules/logger';
import {
  createInlineBookmark,
  getFirstInlineBookmarkInPath,
  getInlineBookmarkInSameParagraph,
} from '../inlineBookmark';
import {
  createInlineParagraphWithChildren,
  getParentParagraph,
} from 'domains/reporter/RichTextEditor/plugins/paragraph';
import { getNextInsertionPoint } from 'domains/reporter/RichTextEditor/utils/getNextInsertionPoint';

const getTextNode = (node: DeepLink, associatedStudy?: ReviewedStudyByDate) => {
  switch (node.variant) {
    case DeepLinkVariants.IMAGE_SLICE:
      return {
        text:
          node.context.seriesDisplayName != null
            ? `(series ${node.context.seriesDisplayName}, image ${node.context.imageNumber})`
            : `(image ${node.context.imageNumber})`,
      };
    case DeepLinkVariants.POINT:
      return {
        text: node.context.label,
      };
    case DeepLinkVariants.STUDY: {
      if (associatedStudy == null) break;

      const { studyDate, description } = associatedStudy;
      const formattedDate = studyDate != null ? toLocaleString(studyDate) : MISSING_STUDY_DATE;
      const label = `${description ?? MISSING_STUDY_DESCRIPTION} ${formattedDate ?? MISSING_STUDY_DATE}`;
      return {
        text: label,
      };
    }
    default:
      unreachableCaseError((node as DeepLinkPluginElement).variant);
  }
};

/**
 * Insert a node where ever the current selection is in the editor.
 * @param {Editor} editor The editor to insert into.
 */
export const insertDeepLink = ({
  editor,
  at,
  node,
}: {
  editor: Editor;
  node: DeepLink;
  at?: Path | Point | Range | undefined;
}) => {
  //If this is an image slice variant - use that context to generate the text?
  Transforms.insertNodes(
    editor,
    {
      type: DEEP_LINK_PLUGIN_ID,
      ...node,
      children: [getTextNode(node)],
    },
    {
      at,
    }
  );
};

export const generateComparisonDeepLinkNode = (study: ReviewedStudyByDate): DeepLink => {
  return {
    target: PageTypes.VIEWER,
    variant: DeepLinkVariants.STUDY,
    context: {
      studySmid: study.smid,
    },
  };
};

export const isStudyDeepLink = (node: Node | DeepLink): boolean =>
  node != null &&
  node.variant === DeepLinkVariants.STUDY &&
  node.type === DEEP_LINK_PLUGIN_ID &&
  node.context?.studySmid != null &&
  typeof node.context.studySmid === 'string';

export const isOlderDeepLink = (
  node: Node | DeepLink,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  studyDate?: Date | number | null
): boolean => {
  if (node == null || isStudyDeepLink(node) === false) return false;
  const found = reviewedStudies.get(node.context.studySmid);
  if (found == null) return false;
  return (
    compareDesc(
      new Date(found.studyDate ?? DEFAULT_STUDY_DATE_VALUE),
      new Date(studyDate ?? DEFAULT_STUDY_DATE_VALUE)
    ) > 0
  );
};

export const isNewerDeepLink = (
  node: Node | DeepLink,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  studyDate?: Date | number | null
): boolean => {
  if (node == null || isStudyDeepLink(node) === false) return false;
  const found = reviewedStudies.get(node.context.studySmid);
  if (found == null) return false;
  return (
    compareAsc(
      new Date(found.studyDate ?? DEFAULT_STUDY_DATE_VALUE),
      new Date(studyDate ?? DEFAULT_STUDY_DATE_VALUE)
    ) > 0
  );
};

export const createDeepLinkWrapper = (deepLink: DeepLink): DeepLinkPluginElement => {
  return {
    type: DEEP_LINK_PLUGIN_ID,
    ...deepLink,
    children: [],
  };
};

/**
 * Find a node in the editor by its guid
 * @param {Editor} editor The editor to search in
 */
export const findDeepLink = ({
  editor,
  node,
}: {
  editor: Editor;
  node: DeepLink;
}): Nullable<NodeEntry<DeepLinkPluginElement>> =>
  find<DeepLinkPluginElement>(
    editor,
    (n: Node) =>
      n.type === DEEP_LINK_PLUGIN_ID &&
      n.variant === node.variant &&
      n.target === node.target &&
      equals((n as DeepLinkPluginElement).context, node.context),
    { at: [] }
  );

export const isValidDeepLink: (payload: DeepLink) => boolean = (payload) => {
  return (
    ['viewer', 'worklist'].includes(payload.target) &&
    ['imageSlice', 'point', 'study'].includes(payload.variant)
  );
};

export const isReplaceableDefaultComparisonText = (text: string): boolean => {
  const normalized = text.trim().toLowerCase().replace(/\.+$/, '').trim();
  return REPLACEABLE_DEFAULT_COMPARISON_TEXTS.includes(normalized);
};

// Keep the radiologist's text and the appended chip from running together, e.g.
// "Prior films reviewed:" + chip → "Prior films reviewed: [CT 1/2/2024]".
const insertSeparatorBeforeAppendedDeepLink = (editor: Editor, bookmark: NodeEntry<Node>): void => {
  const text = Node.string(bookmark[0]);
  if (text === '' || /\s$/.test(text)) return;
  // Anchor on the bookmark's last *text* child rather than Editor.end, so a nested inline element
  // (picklist, placeholder) can't swallow the space.
  const [lastNode, lastPath] = Editor.last(editor, bookmark[1]);
  if (!Text.isText(lastNode)) return;
  Transforms.insertText(editor, ' ', { at: { path: lastPath, offset: lastNode.text.length } });
};

export const getExistingBookmarkWithStudyDeepLinks = (
  editor: Editor,
  start: Path = [],
  end: Path = []
): NodeEntry<DeepLinkPluginElement | Node> | void | null => {
  const maybeDeepLinkStudyNodes = Editor.nodes(editor, {
    at: [start, end],
    match: isStudyDeepLink,
  }).next();
  if (maybeDeepLinkStudyNodes.done) {
    return null;
  }
  const maybeComparisonFieldWithDeepLinks = Editor.above(editor, {
    at: maybeDeepLinkStudyNodes.value[1],
    mode: 'lowest',
    match: (node: Node) => node.type === INLINE_BOOKMARK_PLUGIN_ID,
  });
  if (maybeComparisonFieldWithDeepLinks == null) {
    return null;
  }
  return maybeComparisonFieldWithDeepLinks;
};

const getEligibleReviewedStudies = ({
  reviewedStudies,
  currentStudySmid,
  currentComparativeStudySmids,
  allowStaleComparatives,
  linkedSmids,
}: {
  reviewedStudies: Map<string, ReviewedStudyByDate>;
  currentStudySmid?: string | null;
  currentComparativeStudySmids?: readonly string[] | null;
  allowStaleComparatives: boolean;
  linkedSmids?: ReadonlySet<string>;
}): Array<[string, ReviewedStudyByDate]> =>
  Array.from(reviewedStudies).filter(([smid]) => {
    // When the study in question is the primary study, do not add it.
    // TODO: This should be handled upstream by the logic that is providing the reviewed studies.
    //       If that is fixed, we could remove this check.
    if (smid === currentStudySmid) {
      logger.warn(
        '[populateComparisonStudyNodes] Skipping insertion of the primary study into the comparison field'
      );
      return false;
    }

    // Linked studies are current exams and must never appear in the comparison field.
    if (linkedSmids?.has(smid) === true) {
      return false;
    }

    // When the study in question is not a current comparative study, do not add it.
    // This may occur for example if the comparative study was previously added, but the viewer is not open,
    // or the study was removed from the comparisons.
    if (!allowStaleComparatives && !(currentComparativeStudySmids ?? []).includes(smid)) {
      logger.info(
        `[populateComparisonStudyNodes] Not inserting non-active comparative study ${smid}`
      );
      return false;
    }

    return true;
  });

export type PopulateComparisonStudyNodesOptions = {
  allowStaleComparatives?: boolean;
  replaceExistingBookmarkContent?: boolean;
  linkedSmids?: ReadonlySet<string>;
  templateBookmarkTextMap?: TemplateBookmarkTextMap;
};

/**
 * Whether the bookmark's existing text must be kept as-is. Comparison chips are still appended
 * after it — this only means "don't overwrite what the radiologist wrote".
 */
export const shouldPreserveExistingBookmarkContent = ({
  bookmarkExists,
  bookmarkText,
  bookmarkName,
  templateBookmarkTextMap,
  replaceExistingBookmarkContent,
}: {
  bookmarkExists: boolean;
  bookmarkText: string;
  bookmarkName: string | undefined;
  templateBookmarkTextMap: TemplateBookmarkTextMap | undefined;
  replaceExistingBookmarkContent: boolean;
}): boolean => {
  if (replaceExistingBookmarkContent) return false;

  const hasExistingBookmarkContent = bookmarkExists && bookmarkText.trim() !== '';
  if (!hasExistingBookmarkContent) return false;

  // Text is replaceable when it still matches the template that was applied to this report, or
  // when it is a recognized default placeholder. The whitelist has to apply even when the template
  // map has an entry, e.g. a template default of "None available." shortened by hand to "None" is
  // still a placeholder rather than user-authored content.
  const isStillTemplateText =
    isBookmarkTextUnchangedFromTemplate(
      bookmarkName,
      bookmarkText,
      templateBookmarkTextMap ?? new Map()
    ) || isReplaceableDefaultComparisonText(bookmarkText);

  const isUserAuthoredContent = !isStillTemplateText;
  return isUserAuthoredContent;
};

// Remove every study deep-link node from the comparison section, e.g. when there are no reviewed
// studies left to populate and any previously inserted comparison nodes are now stale.
export const removeAllComparisonStudyNodes = (
  editor: Nullable<Editor>,
  comparisonHeadingKeywords: HeadingKeywords['comparison']
): void => {
  if (editor == null || comparisonHeadingKeywords == null || comparisonHeadingKeywords.length < 1)
    return;

  const comparisonHeading = getComparisonHeading(editor, comparisonHeadingKeywords);
  if (comparisonHeading == null) {
    logger.info('[removeAllComparisonStudyNodes] No comparison heading found');
    return;
  }

  const nextHeading = getNextHeadingOrHeadingLikeNode({ editor, location: comparisonHeading[1] });
  const comparisonSectionEndPath = nextHeading?.[1] ?? Editor.last(editor, [])[1];

  const allDeepLinkNodes = Array.from(
    Editor.nodes(editor, {
      at: [comparisonHeading[1], comparisonSectionEndPath],
      match: isStudyDeepLink,
    }) ?? []
  );
  if (allDeepLinkNodes.length === 0) return;

  const nodePathsToRemove: Array<Path> = [];
  for (let i = 0; i < allDeepLinkNodes.length; i++) {
    const [, dlNodePath] = allDeepLinkNodes[i];
    nodePathsToRemove.push(dlNodePath);
    // remove associated text nodes
    const associatedText = Editor.next(editor, {
      at: dlNodePath,
      match: (node: Node) => Text.isText(node),
    });
    if (associatedText != null) {
      if (i === allDeepLinkNodes.length - 1) {
        // clear the text node if it is associated with the last deep link node being removed
        Transforms.setNodes(editor, createEmptyTextNode(), { at: associatedText[1] });
      } else {
        nodePathsToRemove.push(associatedText[1]);
      }
    }
  }
  while (nodePathsToRemove.length > 0) {
    const path = nodePathsToRemove.pop();
    if (path == null) continue;
    Transforms.removeNodes(editor, { at: path });
  }
};

/**
 * Make the comparison bookmark ready to receive study chips, by emptying the bookmark we found —
 * or creating a bookmark paragraph when the comparison section has none yet.
 *
 * Only call this for content that may be overwritten, i.e. template or default placeholder text —
 * see {@link shouldPreserveExistingBookmarkContent}.
 *
 * @returns the emptied bookmark. It is re-found rather than passed through, because the transforms
 *          here replace the node and invalidate the caller's entry.
 */
export const clearOrCreateComparisonBookmark = ({
  editor,
  comparisonHeadingPath,
  firstInlineBookmark,
}: {
  editor: Editor;
  comparisonHeadingPath: Path;
  firstInlineBookmark: NodeEntry<Node> | null;
}): NodeEntry<Node> | undefined => {
  Editor.withoutNormalizing(editor, () => {
    const nextInsertionPoint = getNextInsertionPoint(editor, comparisonHeadingPath);
    const bookmarkParentParagraph =
      firstInlineBookmark != null ? getParentParagraph(editor, firstInlineBookmark[1]) : null;

    // If there are no paragraphs with any bookmarks, create one
    if (bookmarkParentParagraph == null) {
      const emptyInlineBookmark = createInlineParagraphWithChildren([createInlineBookmark()]);
      Transforms.insertNodes(editor, emptyInlineBookmark, {
        at: nextInsertionPoint,
      });
      return;
    }

    if (firstInlineBookmark == null) return;

    // Wipe out the existing inline bookmark
    // First, remove the inline bookmark node entirely.
    Transforms.removeNodes(editor, { at: firstInlineBookmark[1] });
    // Then, replace it with a clone that has no children.
    Transforms.insertNodes(
      editor,
      { ...firstInlineBookmark[0], children: [{ text: '' }] },
      { at: firstInlineBookmark[1] }
    );
  });

  return Editor.next(editor, {
    at: comparisonHeadingPath,
    match: (node: Node) => node.type === INLINE_BOOKMARK_PLUGIN_ID,
  });
};

/** Populate after resetAndPopulateComparisonStudyNodes has cleared existing study links. */
const populateComparisonStudyNodes = (
  editor: Nullable<Editor>,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  comparisonHeadingKeywords: HeadingKeywords['comparison'],
  currentStudySmid?: string | null,
  currentComparativeStudySmids?: readonly string[] | null,
  options?: PopulateComparisonStudyNodesOptions
): void => {
  const allowStaleComparatives = options?.allowStaleComparatives ?? false;
  const replaceExistingBookmarkContent = options?.replaceExistingBookmarkContent ?? false;
  if (
    editor == null ||
    reviewedStudies == null ||
    reviewedStudies.size < 1 ||
    comparisonHeadingKeywords == null ||
    comparisonHeadingKeywords.length < 1
  )
    return;

  // Don't auto-fill if we can't find a comparison heading or heading-like node
  const comparisonHeading = getComparisonHeading(editor, comparisonHeadingKeywords);
  if (comparisonHeading == null) {
    logger.info('[populateComparisonStudyNodes] No comparison heading found');
    return;
  }

  const eligibleReviewedStudies = getEligibleReviewedStudies({
    reviewedStudies,
    currentStudySmid,
    currentComparativeStudySmids,
    allowStaleComparatives,
    linkedSmids: options?.linkedSmids,
  });

  /**
   * Target logic (find the target bookmark to insert into)
   */
  // Get the next heading
  const nextHeading = getNextHeadingOrHeadingLikeNode({ editor, location: comparisonHeading[1] });
  const comparisonSectionEndPath = nextHeading?.[1] ?? Editor.last(editor, [])[1];

  if (eligibleReviewedStudies.length === 0) {
    return;
  }

  // A heading-like paragraph can contain both the label and the target field, e.g.
  // "COMPARISON: [None]". Prefer that inline bookmark so default text is cleared
  // instead of inserting into a later bookmark in the same section.
  const firstInlineBookmark =
    getInlineBookmarkInSameParagraph(editor, comparisonHeading[1]) ??
    getFirstInlineBookmarkInPath(editor, comparisonHeading[1], comparisonSectionEndPath);
  const preserveExistingContent = shouldPreserveExistingBookmarkContent({
    bookmarkExists: firstInlineBookmark != null,
    bookmarkText: firstInlineBookmark != null ? Node.string(firstInlineBookmark[0]) : '',
    bookmarkName:
      firstInlineBookmark != null
        ? (firstInlineBookmark[0] as InlineBookmarkPluginElement).name
        : undefined,
    templateBookmarkTextMap: options?.templateBookmarkTextMap,
    replaceExistingBookmarkContent,
  });

  let targetBookmark: NodeEntry<Node> | undefined;
  if (preserveExistingContent && firstInlineBookmark != null) {
    // Keep what the radiologist wrote and append the studies after it, rather than skipping
    // population altogether.
    logger.info(
      '[populateComparisonStudyNodes] Appending comparison studies after existing user content'
    );
    insertSeparatorBeforeAppendedDeepLink(editor, firstInlineBookmark);
    // No mutation moved the bookmark, so the captured entry's path is still valid.
    targetBookmark = firstInlineBookmark;
  } else {
    targetBookmark = clearOrCreateComparisonBookmark({
      editor,
      comparisonHeadingPath: comparisonHeading[1],
      firstInlineBookmark,
    });
  }

  if (targetBookmark == null) {
    logger.warn('[populateComparisonStudyNodes] Unable to locate target bookmark for comparison');
    return;
  }

  const firstNode = Editor.first(editor, targetBookmark[1]);
  if (firstNode == null) return;
  const firstNodePath = Editor.before(editor, firstNode[1]);
  if (firstNodePath == null) return;
  const lastNode = Editor.last(editor, targetBookmark[1]);
  if (lastNode == null) return;
  const lastNodePath = Editor.after(editor, lastNode[1]);
  if (lastNodePath == null) return;

  /**
   * Insertion logic (insert reviewed studies missing in the comparison field)
   */
  for (const [smid, study] of eligibleReviewedStudies) {
    // check if the study is already in the comparison field
    const sameStudy = Editor.next(editor, {
      at: firstNodePath,
      match: (node: Node | DeepLink) => {
        if (!isStudyDeepLink(node)) return false;
        return smid === node.context.studySmid;
      },
    });
    if (sameStudy != null) {
      continue;
    }

    const deepLinkNode = generateComparisonDeepLinkNode(study);
    const nodeToInsert = {
      type: DEEP_LINK_PLUGIN_ID,
      ...deepLinkNode,
      children: [getTextNode(deepLinkNode, study)],
    } as const;

    // find the next deep link node that is older than the current item
    const nextDeepLinkNode = Editor.next(editor, {
      at: firstNodePath,
      match: (node: Node) => isOlderDeepLink(node, reviewedStudies, study.studyDate),
    });
    // find the previous deep link node that is newer than the current item
    const prevDeepLinkNode = Editor.previous(editor, {
      at: lastNodePath,
      match: (node: Node) => isNewerDeepLink(node, reviewedStudies, study.studyDate),
    });

    // if inserting as oldest one in the comparison field (last node)
    if (nextDeepLinkNode == null) {
      const textNode = Editor.previous(editor, {
        at: lastNodePath,
        match: (node: Node) => Text.isText(node),
      });
      if (textNode == null) continue;

      const targetPath = textNode[1];
      targetPath[targetPath.length - 1] += 1;
      const hasTrailingComma = textNode[0].text.trim().endsWith(',');
      if (prevDeepLinkNode == null || hasTrailingComma) {
        // if there are no deep link nodes in the comparison field or the previous text node ends with a comma
        Transforms.insertNodes(editor, [nodeToInsert, createEmptyTextNode()], { at: targetPath });
      } else {
        // if the previous text node does not end with a comma, insert a comma
        Transforms.insertNodes(
          editor,
          [createDelimiterTextNode(), nodeToInsert, createEmptyTextNode()],
          {
            at: targetPath,
          }
        );
      }
      continue;
    }

    // if inserting into other positions in the comparison field
    const textNode = Editor.previous(editor, {
      at: nextDeepLinkNode[1],
      match: (node: Node) => Text.isText(node),
    });
    if (textNode == null) continue;

    const targetPath = textNode[1];
    targetPath[targetPath.length - 1] += 1;
    Transforms.insertNodes(editor, [nodeToInsert, createDelimiterTextNode()], {
      at: targetPath,
    });
  }
};

/** Clear stale comparison links even when there are no studies eligible to replace them. */
export function resetAndPopulateComparisonStudyNodes(
  editor: Nullable<Editor>,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  comparisonHeadingKeywords: HeadingKeywords['comparison'],
  currentStudySmid?: string | null,
  currentComparativeStudySmids?: readonly string[] | null,
  options?: PopulateComparisonStudyNodesOptions
): void {
  removeAllComparisonStudyNodes(editor, comparisonHeadingKeywords);
  populateComparisonStudyNodes(
    editor,
    reviewedStudies,
    comparisonHeadingKeywords,
    currentStudySmid,
    currentComparativeStudySmids,
    options
  );
}

export const getDeepLinkKey = (
  editor: Editor | null,
  element: DeepLinkPluginElement | null
): Key | null => {
  if (editor == null || element == null) return null;
  try {
    return ReactEditor.findKey(editor, element);
  } catch (e: any) {
    logger.error('[getDeepLinkKey] Failed to get key for deep link element', e);
    return null;
  }
};
